import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import Pagination from 'components/Pagination';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Card,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from 'reactstrap';

import { getParamsFormObject, handleError } from 'utils';

import discoverProvince from 'api/discoverProvince';
import images from 'assets/images';
import AppCloseModalButton from 'components/App/AppCloseModalButton';
import Loading from 'components/App/Loading';
import DiscoverProvinceDetails from 'components/DiscoverProvinceDetails';
import ModalConfirmDiscoverProvince from 'components/Modals/ModalConfirmDiscoverProvince';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import { toast } from 'react-toastify';
import ENDPOINT from 'constants/endPoint';
import styles from './ManagerDiscoveryProvinces.module.scss';

const TITLE = 'Quản lý điểm đến';

const NAV_TAB = [
  {
    title: 'Tất cả',
    isPublished: undefined,
  },
  {
    title: 'Đã xuất bản',
    isPublished: true,
  },
  {
    title: 'Chưa xuất bản',
    isPublished: false,
  },
];

const breadcrumbList = [
  {
    name: 'Trang chủ',
    isActive: false,
    link: '', // TODO: ADD ROUTE WHEN HAVE LINK
  },
  {
    name: TITLE,
    isActive: true,
    link: '',
  },
];

const DEFAULT_LIMIT = 10;

const getHref = (id) => `/manager-discovery/destination/${id}`;

const parseIsPublishedParam = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const buildDiscoveryProvincesPayload = ({ page, q, isPublished }) => {
  const payload = {
    page,
    limit: DEFAULT_LIMIT,
  };

  if (q) {
    payload.q = q;
  }

  if (isPublished !== undefined) {
    payload.isPublished = isPublished;
  }

  return payload;
};

function ManagerDiscoveryProvinces(props) {
  const {
    provincesLoading,
    provinces,
    provinceDetailsLoading,
    provinceDetails,

    actionGetDiscoveryProvinces,
    actionCleanupDiscoveryProvinces,
    actionGetDiscoveryProvinceDetails,
    actionCleanupDiscoverProvinceDetails,
  } = props;

  const history = useHistory();
  const tableRef = React.useRef(null);

  const [isOpenSuccessModal, setIsOpenSuccessModal] = React.useState(false);

  const params = useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const searchQueries = React.useMemo(
    () => ({
      page: params.get('page') || 1,
      q: params.get('q') || '',
      limit: DEFAULT_LIMIT,
      isPublished: parseIsPublishedParam(params.get('isPublished')),
    }),
    [params],
  );

  const [isOpenCreateProvince, setIsOpenCreateProvince] = React.useState(false);
  const [editingProvinceId, setEditingProvinceId] = React.useState(null);
  const [deleteProvince, setDeleteProvince] = React.useState({
    id: '',
    isOpen: false,
    isLoading: false,
  });
  const [search, setSearch] = React.useState(searchQueries.q);

  const onPushSearchQueries = React.useCallback(
    (queries) => {
      const formData = {
        ...searchQueries,
        ...queries,
      };

      const urlParams = {
        page: formData.page,
        q: formData.q,
        limit: DEFAULT_LIMIT,
      };

      if (formData.isPublished !== undefined) {
        urlParams.isPublished = String(formData.isPublished);
      }

      history.push({ search: getParamsFormObject(urlParams) });
    },
    [history, searchQueries],
  );

  const refetchProvincesOnPageOne = React.useCallback(() => {
    if (+searchQueries.page === 1) {
      actionGetDiscoveryProvinces(
        buildDiscoveryProvincesPayload({
          ...searchQueries,
          page: 1,
        }),
      );
    } else {
      onPushSearchQueries({ page: 1 });
    }
  }, [actionGetDiscoveryProvinces, onPushSearchQueries, searchQueries]);

  const onChangePage = React.useCallback(
    (selected) => {
      onPushSearchQueries({ page: selected.selected + 1 });
    },
    [onPushSearchQueries],
  );

  const onCloseDeleteProvince = React.useCallback(() => {
    setDeleteProvince((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const onCloseCreateProvince = React.useCallback(() => {
    setIsOpenCreateProvince(false);
    setEditingProvinceId(null);
    actionCleanupDiscoverProvinceDetails();
  }, [actionCleanupDiscoverProvinceDetails]);

  const onOpenCreateProvince = React.useCallback(() => {
    actionCleanupDiscoverProvinceDetails();
    setEditingProvinceId(null);
    setIsOpenCreateProvince(true);
  }, [actionCleanupDiscoverProvinceDetails]);

  const onOpenEditProvince = React.useCallback(
    (id) => () => {
      actionCleanupDiscoverProvinceDetails();
      setIsOpenCreateProvince(false);
      setEditingProvinceId(id);
      actionGetDiscoveryProvinceDetails(id);
    },
    [
      actionCleanupDiscoverProvinceDetails,
      actionGetDiscoveryProvinceDetails,
    ],
  );

  const onSubmitDeleteProvince = React.useCallback(async () => {
    try {
      setDeleteProvince((prev) => ({ ...prev, isLoading: true }));

      await discoverProvince.deleteDiscoverProvince(deleteProvince.id);

      toast.success('Xóa điểm đến thành công');

      if (deleteProvince.isOpen) {
        setDeleteProvince((prev) => ({
          ...prev,
          isOpen: false,
          id: '',
        }));
      }
      refetchProvincesOnPageOne();
    } catch (error) {
      handleError(error);
    } finally {
      setDeleteProvince((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    deleteProvince.id,
    deleteProvince.isOpen,
    refetchProvincesOnPageOne,
  ]);

  const onChangeTab = React.useCallback(
    (isPublished) => () => {
      onPushSearchQueries({ isPublished, page: 1 });
    },
    [onPushSearchQueries],
  );

  const handleCloseSuccessModal = React.useCallback(() => {
    setIsOpenSuccessModal(false);
  }, []);

  const handleAddDiscoveryPlace = React.useCallback(() => {
    setIsOpenSuccessModal(false);
    history.push(ENDPOINT.MANAGER_DISCOVERY_PLACE_CREATE);
  }, []);

  const totalPage = React.useMemo(
    () => Math.ceil((provinces.total || 0) / DEFAULT_LIMIT),
    [provinces.total],
  );

  React.useEffect(() => {
    actionGetDiscoveryProvinces(buildDiscoveryProvincesPayload(searchQueries));
  }, [actionGetDiscoveryProvinces, searchQueries]);

  React.useEffect(
    () => () => {
      actionCleanupDiscoveryProvinces();
    },
    [actionCleanupDiscoveryProvinces],
  );

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name={TITLE} />

        <Container fluid>
          <Breadcrumb pageTitle={TITLE} breadcrumbList={breadcrumbList} />

          <Loading loading={provincesLoading} loaderOnPage />

          <Card className="Section">
            <Row>
              <Col md="6">
                <Label className="Section__Search__Label">Tìm kiếm</Label>

                <div className="position-relative">
                  <Input
                    placeholder="Tìm kiếm hạng mục"
                    style={{ paddingLeft: '36px' }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onPushSearchQueries({ q: search, page: 1 });
                      }
                    }}
                  />

                  <i className="fas fa-search Section__Search__Icon__Search" />
                </div>
              </Col>

              <Col md="6">
                <div className="d-flex align-items-end h-100 mb-24 mb-md-0">
                  <Button
                    onClick={() => onPushSearchQueries({ q: search, page: 1 })}
                    style={{ minWidth: '119px' }}
                  >
                    Tìm kiếm
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>

          <Card className="Section">
            <div className="d-flex flex-column flex-md-row gap-4 justify-content-between custom_pills">
              <Nav pills>
                {NAV_TAB.map((tab) => (
                  <NavItem key={tab.title}>
                    <NavLink
                      className={
                        searchQueries.isPublished === tab.isPublished
                          ? 'active'
                          : ''
                      }
                      onClick={onChangeTab(tab.isPublished)}
                    >
                      <span>{tab.title}</span>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <Button
                classes="Button__Add"
                fontSize="13px"
                onClick={onOpenCreateProvince}
              >
                <i className="fas fa-plus" />
                Thêm điểm đến
              </Button>
            </div>

            <Label className="Table__Body__Total">
              {`${provinces?.total || 0} điểm đến`}
            </Label>

            <div ref={tableRef} className="w-100" style={{ overflow: 'auto' }}>
              <table className="table custom_table">
                <thead>
                  <tr>
                    <th>Điểm đến</th>
                    <th>Hạng mục bao gồm</th>
                    <th>Tổng điểm khám phá</th>
                    <th>Trạng thái</th>
                    <th>Tổng lượt đánh giá</th>
                  </tr>
                </thead>
                <tbody>
                  {provinces?.data.map((item) => (
                    <tr key={item._id} style={{ cursor: 'default' }}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div className="d-flex flex-column gap-1 buttons-group">
                            <div>{item.name}</div>
                            <div>
                              <div className="buttons-action mt-2 d-flex flex-wrap column-gap-3 row-gap-2">
                                <button
                                  type="button"
                                  className="button-action button-destructive"
                                  onClick={() =>
                                    setDeleteProvince({
                                      id: item._id,
                                      isOpen: true,
                                    })
                                  }
                                >
                                  Xoá
                                </button>

                                <Link to={getHref(item._id)}>
                                  <button
                                    type="button"
                                    className="button-action button-link text-start"
                                  >
                                    Xem chi tiết
                                  </button>
                                </Link>

                                <button
                                  type="button"
                                  className="button-action button-link text-start"
                                  onClick={onOpenEditProvince(item._id)}
                                >
                                  Chỉnh sửa
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {item.discoverCategories &&
                          item.discoverCategories.length > 0 && (
                            <div className={styles.CategoryList}>
                              {item.discoverCategories.map((category) => (
                                <span key={category._id}>{category.name}</span>
                              ))}
                            </div>
                        )}
                      </td>
                      <td>{item.placeCount}</td>
                      <td>
                        {item.isPublished ? (
                          <span className="text-success">Đã xuất bản</span>
                        ) : (
                          <span className="text-danger">Chưa xuất bản</span>
                        )}
                      </td>
                      <td>{item.reviewCount}</td>
                    </tr>
                  ))}

                  {provinces?.data.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center">
                        Không có dữ liệu
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            page={searchQueries.page}
            pageCount={totalPage}
            onChangePage={onChangePage}
          />
        </Container>
      </div>

      <Modal
        isOpen={isOpenCreateProvince || !!editingProvinceId}
        toggle={onCloseCreateProvince}
        size="md"
        fade
        style={{ maxWidth: '779px' }}
      >
        <ModalHeader
          toggle={onCloseCreateProvince}
          close={<AppCloseModalButton onClick={onCloseCreateProvince} />}
          className="justify-content-between"
        >
          {editingProvinceId ? 'Chỉnh sửa điểm đến' : 'Tạo điểm đến'}
        </ModalHeader>

        <ModalBody>
          {editingProvinceId && provinceDetailsLoading && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner color="primary" />
            </div>
          )}

          {editingProvinceId
            && !provinceDetailsLoading
            && (!provinceDetails._id
              || provinceDetails._id !== editingProvinceId) && (
              <p className="text-center text-danger mb-0">
                Không tải được chi tiết điểm đến
              </p>
          )}

          {!editingProvinceId && (
            <DiscoverProvinceDetails
              isCreate
              callback={() => {
                setIsOpenCreateProvince(false);
                refetchProvincesOnPageOne();
                setIsOpenSuccessModal(true);
              }}
            />
          )}

          {editingProvinceId
            && !provinceDetailsLoading
            && provinceDetails._id === editingProvinceId && (
              <DiscoverProvinceDetails
                isCreate={false}
                provinceId={editingProvinceId}
                provinceDetails={provinceDetails}
                callback={() => {
                  setEditingProvinceId(null);
                  actionCleanupDiscoverProvinceDetails();
                  actionGetDiscoveryProvinces(
                    buildDiscoveryProvincesPayload({
                      ...searchQueries,
                      page: searchQueries.page,
                    }),
                  );
                }}
              />
          )}
        </ModalBody>
      </Modal>

      <ModalRequestAction
        isOpen={deleteProvince.isOpen}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xoá \n điểm đến này không?'}
        onToggle={onCloseDeleteProvince}
        onCloseModal={onCloseDeleteProvince}
        onConfirm={onSubmitDeleteProvince}
      />

      <ModalConfirmDiscoverProvince
        isOpen={isOpenSuccessModal}
        onCloseModal={handleCloseSuccessModal}
        onAddDiscoveryPlace={handleAddDiscoveryPlace}
      />
    </>
  );
}

ManagerDiscoveryProvinces.propTypes = {
  provincesLoading: PropTypes.bool.isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  provinceDetailsLoading: PropTypes.bool.isRequired,
  provinceDetails: PropTypes.instanceOf(Object).isRequired,

  actionGetDiscoveryProvinces: PropTypes.func.isRequired,
  actionCleanupDiscoveryProvinces: PropTypes.func.isRequired,
  actionGetDiscoveryProvinceDetails: PropTypes.func.isRequired,
  actionCleanupDiscoverProvinceDetails: PropTypes.func.isRequired,
};

export default ManagerDiscoveryProvinces;
